import { AgentCallingModals } from "components/chat/CallAgentModals";
import { MeetWithCPA } from "components/chat/MeetWithCPA";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { User } from "components/icons/Navbar/User";
import { Padlock } from "components/icons/Padlock";
import { PhoneCallIcon } from "components/icons/PhoneCallIcon";
import { CALL_US_CLICKED } from "constants/analyticsEvents";
import {
  ANAND_GENERIC_MEET_LINK,
  INKLE_CONSULTATION,
} from "constants/meetingRequestUrls";
import { useAnalytics } from "hooks/useAnalytics";
import { useModal } from "hooks/useModal";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import CalendarBlank from "static/images/CalendarBlank.svg";
import CalendarMoney from "static/images/CalendarMoney.svg";
import UsersThree from "static/images/UsersThree.svg";
import "static/styles/containers/dashboard.css";
import { openMeetWithTranqModal } from "store/slices/chat";
import { GetHelp } from "components/icons/GetHelp";
import { useCheckAgentsAvailabilityQuery } from "store/apis/agents";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import ToolTip from "components/design/toolTip";
import { PLATFORM } from "constants/addons";
import { useParams } from "react-router-dom";
import { SubscriptionInProfile } from "types/Models/subscription";
import authContext from "jwt_context&axios/authContext";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { TaxSubscriptionModal } from "pages/Tax/TaxSubscriptionModal";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Loader from "components/design/loader";
import { Button } from "components/DesignSystem/Button/Button";
import ConditionalToolTip from "components/design/conditionalToolTip";
import classNames from "classnames";

export const CallUs = ({
  shouldShowUpgrade: shouldShowUpgradeFromProps,
  entityId,
}: {
  shouldShowUpgrade: boolean;
  entityId?: string;
}) => {
  const dispatch = useDispatch();
  const meetWithCpaModal = useModal();
  const agentCallingModals = useModal();
  const { trackEvent } = useAnalytics();

  const [open, setOpen] = useState(false);
  const group = useCurrentGroupContext();
  const { authtoken } = useContext(authContext);

  const { groupId: currentGroup } = useParams<{ groupId?: string }>();
  const { platform_subscription: groupPlatformSubscription } =
    useCurrentGroupContext();

  let {
    platform_subscription: currentSubscription,
  }: {
    platform_subscription: SubscriptionInProfile;
  } = authtoken || {};

  if (currentGroup) {
    currentSubscription = groupPlatformSubscription;
  }

  const {
    data: subscribedPlansInlcudingInactive,
    isSuccess: isTaxSubscriptionSuccess,
  } = useGetSubscriptionsQuery({
    groupId: group.uuid,
    entityId,
    subscription_types: PLATFORM,
    get_inactive: true,
  });

  const currentAppSubscription = subscribedPlansInlcudingInactive?.find(
    (item) => item.uuid === currentSubscription?.subscription_uuid
  );

  const taxSubscriptionModals = useModal();
  const onUpgrade = () => {
    setOpen(false);
    taxSubscriptionModals.open();
  };

  const { data: availableAgents, isLoading } = useCheckAgentsAvailabilityQuery(
    {
      groupId: group.uuid,
    },
    { skip: !group.uuid || !currentAppSubscription }
  );
  const isAgentAvailable =
    availableAgents && availableAgents.available_agent_count > 0;

  return (
    <>
      <Dropdown.Root
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (isOpen) trackEvent(CALL_US_CLICKED);
        }}
      >
        <Dropdown.Trigger asChild className="t-group">
          <div
            className="t-rounded  hover:t-bg-neutral-0 t-p-2 t-text-text-60 t-text-subtext-sm"
            onClick={(e) => e.stopPropagation()}
            role="button"
          >
            <div className="t-flex t-items-center t-gap-2">
              <div className="t-flex">
                <GetHelp />
                {availableAgents?.available_agent_count! > 0 && (
                  <span className="t-relative t-flex t-size-1.5">
                    <span className="t-absolute t-inline-flex t-h-full t-w-full t-animate-ping t-rounded-full t-bg-green-55 t-opacity-75"></span>
                    <span className="t-rt-elative t-inline-flex t-size-1.5 t-rounded-full t-bg-green-55"></span>
                  </span>
                )}
              </div>
              Get help
            </div>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content sideOffset={4} align="end" side="bottom">
            <Async.Root
              isLoading={Boolean(currentAppSubscription) && isLoading}
              isSuccess={isTaxSubscriptionSuccess}
              customLoader={
                <div className="t-px-3 t-py-1 t-flex t-items-center t-gap-2 t-text-neutral-40">
                  <Loader customType="secondary" size="small" />
                  <div className="t-text-body-sm t-leading-[12px]">
                    Loading...
                  </div>
                </div>
              }
              isEmpty={false}
            >
              <Async.Success>
                {currentAppSubscription ? (
                  <>
                    <ConditionalToolTip
                      condition={
                        !isAgentAvailable && (
                          <div className="t-max-w-72 t-text-center">
                            We're offline right now. Leave a message on chat,
                            and we'll reply within 24 hours.
                          </div>
                        )
                      }
                      side="bottom"
                    >
                      <Dropdown.Item
                        icon={
                          <div
                            className={classNames({
                              "t-text-neutral-30": !isAgentAvailable,
                            })}
                          >
                            <PhoneCallIcon />
                          </div>
                        }
                        disabled={!isAgentAvailable}
                        onSelect={agentCallingModals.open}
                      >
                        Call Inkle Support
                      </Dropdown.Item>
                    </ConditionalToolTip>
                  </>
                ) : (
                  <ToolTip
                    side="bottom"
                    text={
                      <div className="t-flex t-flex-col t-gap-4 t-text-left t-w-[264px]">
                        <div className="t-flex t-flex-col t-gap-2">
                          <p className="t-m-0 t-text-subtitle-sm">
                            Upgrade to unlock
                          </p>
                          <p className="t-m-0 t-text-body-sm t-break-keep">
                            You to need to upgrade to Inkle Tax plan to access
                            this feature.
                          </p>
                        </div>
                        <Button onClick={onUpgrade} size="small">
                          Upgrade
                        </Button>
                      </div>
                    }
                  >
                    <Dropdown.Item
                      icon={
                        <div className="t-text-neutral-30">
                          <PhoneCallIcon />
                        </div>
                      }
                      className=" t-flex t-items-center t-w-full"
                      disabled
                    >
                      Call Inkle Support
                      <div className=" t-ml-5">
                        <Padlock />
                      </div>
                    </Dropdown.Item>
                  </ToolTip>
                )}
              </Async.Success>
            </Async.Root>

            <Dropdown.Sub>
              <Dropdown.SubTrigger
                icon={
                  <img
                    src={CalendarBlank}
                    width="20px"
                    height="20px"
                    alt="meeting"
                  />
                }
                asChild
              >
                Book a meeting
              </Dropdown.SubTrigger>
              <Dropdown.Portal>
                <Dropdown.SubContent sideOffset={4}>
                  <Dropdown.Item
                    icon={
                      <img
                        src={UsersThree}
                        width="20px"
                        height="20px"
                        alt="inkleConsultation"
                      />
                    }
                    onSelect={() =>
                      dispatch(openMeetWithTranqModal(INKLE_CONSULTATION))
                    }
                  >
                    Meet Inkle Support
                  </Dropdown.Item>
                  <Dropdown.Item
                    icon={
                      <img
                        src={CalendarMoney}
                        width="20px"
                        height="20px"
                        alt="cpaConsultation"
                      />
                    }
                    onSelect={meetWithCpaModal.open}
                  >
                    Meet with CPA
                  </Dropdown.Item>
                  <Dropdown.Item
                    icon={<User size="20" />}
                    onSelect={() =>
                      dispatch(openMeetWithTranqModal(ANAND_GENERIC_MEET_LINK))
                    }
                  >
                    Meet with CEO
                  </Dropdown.Item>
                </Dropdown.SubContent>
              </Dropdown.Portal>
            </Dropdown.Sub>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <AgentCallingModals
        close={agentCallingModals.close}
        isOpen={agentCallingModals.isOpen}
      />
      <TaxSubscriptionModal
        show={taxSubscriptionModals.isOpen}
        onClose={() => taxSubscriptionModals.close()}
        entityId={entityId || ""}
        groupId={currentGroup || ""}
      />
      <MeetWithCPA {...meetWithCpaModal} />
    </>
  );
};
